import { RewardsMetadataSchema } from "@apps/rewards/schemas/RewardsMetadaSchema";
import {
    array,
    assign,
    boolean,
    literal,
    nullable,
    number,
    object,
    string,
    union,
} from "superstruct";

import { SPENDING_RULES_TYPES } from "../constants";
import {
    RewardsSpendingRuleFixedAmountRewardSchema,
    RewardsSpendingRuleFixedPercentageRewardSchema,
    RewardsSpendingRuleFlexibleAmountRewardSchema,
    RewardsSpendingRuleFlexiblePercentageRewardSchema,
    RewardsSpendingRuleFreeShippingRewardSchema,
    RewardsSpendingRuleGiftCardRewardSchema,
    RewardsSpendingRuleProductRewardSchema,
    RewardsSpendingRuleStoreCreditRewardSchema,
} from "./RewardsSpendingRulesRewardSchema";

//Customer tags schema
const SpendingRuleCustomerTagsSchema = object({
    whitelist: array(string()),
    blacklist: array(string()),
});

//Base spending rules schema
const SpendingRuleBaseSchema = object({
    ruleId: string(),
    versionId: string(),
    title: string(),
    icon: nullable(string()),
    pointsPrice: number(),
    customerTags: SpendingRuleCustomerTagsSchema,
});

//Base points spendable schema
const SpendingRulePointsSpendableSchema = object({
    minSpendablePoints: number(),
    maxSpendablePoints: number(),
});

//Amount discount schema
const SpendingRulesAmountDiscountBaseSchema = assign(
    SpendingRulePointsSpendableSchema,
    object({
        ruleType: literal(SPENDING_RULES_TYPES.amountDiscount),
    })
);

const SpendingRuleFixedAmountDiscountSchema = object({
    metadata: RewardsMetadataSchema,
    rule: assign(SpendingRuleBaseSchema, SpendingRulesAmountDiscountBaseSchema),
    rewards: array(RewardsSpendingRuleFixedAmountRewardSchema),
});

export const SpendingRuleFlexibleAmountDiscountSchema = object({
    metadata: RewardsMetadataSchema,
    rule: assign(SpendingRuleBaseSchema, SpendingRulesAmountDiscountBaseSchema),
    rewards: array(RewardsSpendingRuleFlexibleAmountRewardSchema),
});

//Percentage discount schema
const SpendingRulesPercentageDiscountBaseSchema = assign(
    SpendingRulePointsSpendableSchema,
    object({
        ruleType: literal(SPENDING_RULES_TYPES.percentageDiscount),
    })
);

const SpendingRuleFixedPercentageDiscountSchema = object({
    metadata: RewardsMetadataSchema,
    rule: assign(
        SpendingRuleBaseSchema,
        SpendingRulesPercentageDiscountBaseSchema
    ),
    rewards: array(RewardsSpendingRuleFixedPercentageRewardSchema),
});

export const SpendingRuleFlexiblePercentageDiscountSchema = object({
    metadata: RewardsMetadataSchema,
    rule: assign(
        SpendingRuleBaseSchema,
        SpendingRulesPercentageDiscountBaseSchema
    ),
    rewards: array(RewardsSpendingRuleFlexiblePercentageRewardSchema),
});

//Gift card schema
const SpendingRuleGiftCardSchema = object({
    metadata: RewardsMetadataSchema,
    rule: assign(
        SpendingRuleBaseSchema,
        object({
            ruleType: literal(SPENDING_RULES_TYPES.giftCard),
        })
    ),
    rewards: array(RewardsSpendingRuleGiftCardRewardSchema),
});

//Free shipping schema
const SpendingRuleFreeShippingSchema = object({
    metadata: RewardsMetadataSchema,
    rule: assign(
        SpendingRuleBaseSchema,
        object({
            ruleType: literal(SPENDING_RULES_TYPES.freeShipping),
        })
    ),
    rewards: array(RewardsSpendingRuleFreeShippingRewardSchema),
});

//Free product schema
export const SpendingRuleFreeProductSchema = object({
    metadata: RewardsMetadataSchema,
    rule: assign(
        SpendingRuleBaseSchema,
        object({
            ruleType: literal(SPENDING_RULES_TYPES.freeProduct),
        })
    ),
    rewards: array(RewardsSpendingRuleProductRewardSchema),
});

//Store credit schema
export const SpendingRuleStoreCreditSchema = object({
    metadata: RewardsMetadataSchema,
    rule: assign(
        SpendingRuleBaseSchema,
        object({
            ruleType: literal(SPENDING_RULES_TYPES.storeCredit),
        })
    ),
    rewards: array(RewardsSpendingRuleStoreCreditRewardSchema),
});

export const SpendingRulesStoreCreditResponseSchema = object({
    storeCreditAmount: number(),
});

export const SpendingRulesRefundResponseSchema = object({
    rewardId: string(),
    discountCode: string(),
    success: boolean(),
});

export const SpendingRulesSchema = union([
    SpendingRuleFixedAmountDiscountSchema,
    SpendingRuleFlexibleAmountDiscountSchema,
    SpendingRuleFixedPercentageDiscountSchema,
    SpendingRuleFlexiblePercentageDiscountSchema,
    SpendingRuleGiftCardSchema,
    SpendingRuleFreeShippingSchema,
    SpendingRuleFreeProductSchema,
    SpendingRuleStoreCreditSchema,
]);

export const SpendingRulesResponseSchema = object({
    items: array(SpendingRulesSchema),
});
